import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import type { Season } from "@mysnowscape/snow-lib";

import { multipleChoiceConfirmWrapper } from "../../../../components/Modals/MultipleChoiceConfirmWrapper";
import {
  RouteBuilderProvider,
  useRouteBuilder,
} from "./context/RouteBuilderContext";
import GenerateRoutesControl from "./components/GenerateRoutesControl";
import OptimizationNotice from "./components/OptimizationNotice";
import SetupRouteBuilderModal from "./components/SetupRouteBuilderModal";
import AdjustRouteBuilderModal from "./components/AdjustRouteBuilderModal";

export type RouteBuilderModal = "none" | "setup" | "adjust";
export type RouteBuilderEntryHandle = {
  openEdit: (routeIds: string[]) => Promise<void>;
  openSetup: () => void;
  openDraft: (draftId: string) => Promise<void>;
};

// Modal workflow and URL synchronization.

function RouteBuilderEntryInner({
  modal,
  setModal,
  blankSeason,
  onBlankSessionStarted,
  entryRef,
  syncUrl,
  renderButton = true,
  onOptimizationNoticeChange,
}: {
  modal: RouteBuilderModal;
  setModal: React.Dispatch<React.SetStateAction<RouteBuilderModal>>;
  blankSeason: Season | null;
  onBlankSessionStarted?: () => void;
  entryRef: React.Ref<RouteBuilderEntryHandle>;
  syncUrl: boolean;
  renderButton?: boolean;
  onOptimizationNoticeChange?: (visible: boolean) => void;
}) {
  const {
    hasUnsavedSessionChanges,
    loadDraft,
    openEditSession,
    resetBuilder,
    activeDraftId,
    createBlankSession,
    isLoadingResources,
    builderMode,
    session,
    optimizationBlocked,
    showOptimizationNotice,
    guardOptimization,
  } = useRouteBuilder();
  const [searchParams, setSearchParams] = useSearchParams();

  // Gate URL writes until initial restore finishes. Otherwise modal===none on
  // first paint clears ?builderDraft=… while loadDraft is still in flight.
  const [urlReady, setUrlReady] = useState(!syncUrl);
  const editRouteIds =
    builderMode === "edit" && session
      ? session.routes
          .filter((route) => session.routePersistence?.[route.id])
          .map((route) => session.routePersistence?.[route.id]?._id ?? route.id)
          .join(",")
      : "";
  const showNotice =
    modal === "none" && optimizationBlocked && showOptimizationNotice;

  // The Routes page renders its own Generate Routes button (renderButton is
  // false there). Let it disable that button while the notice shows.
  useEffect(() => {
    onOptimizationNoticeChange?.(showNotice);
  }, [showNotice, onOptimizationNoticeChange]);

  const openSetup = useCallback(() => {
    if (!guardOptimization()) return;
    resetBuilder();
    setModal("setup");
  }, [guardOptimization, resetBuilder, setModal]);

  useImperativeHandle(
    entryRef,
    () => ({
      openEdit: async (routeIds: string[]) => {
        const ok = await openEditSession(routeIds);
        if (ok) setModal("adjust");
      },
      openSetup: () => {
        openSetup();
      },
      openDraft: async (draftId: string) => {
        resetBuilder();
        setModal("adjust");
        const ok = await loadDraft(draftId);
        if (!ok) setModal("none");
      },
    }),
    [openEditSession, openSetup, resetBuilder, loadDraft, setModal],
  );

  // Synchronize initial router search into the builder (e.g. refresh).
  // MemoryRouter in CT keeps this off the iframe URL so the harness does not
  // remount. BrowserRouter in the app still updates the address bar.
  useEffect(() => {
    if (!syncUrl) return undefined;
    let cancelled = false;
    const draftId = searchParams.get("builderDraft");
    const builder = searchParams.get("builder");
    const mode = searchParams.get("builderMode");
    const routeIds = searchParams.get("builderRoutes");

    (async () => {
      try {
        if (draftId) {
          setModal("adjust");
          const ok = await loadDraft(draftId);
          if (cancelled) return;
          setModal(ok ? (builder === "setup" ? "setup" : "adjust") : "none");
        } else if (mode === "edit" && routeIds) {
          setModal("adjust");
          const ok = await openEditSession(routeIds.split(",").filter(Boolean));
          if (cancelled) return;
          setModal(ok ? "adjust" : "none");
        } else if (builder === "setup") {
          if (!cancelled) setModal("setup");
        }
      } finally {
        if (!cancelled) setUrlReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Mount / landing URL only. loadDraft identity changes with companyId;
    // re-running on that would fight an in-progress session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncUrl]);

  // Start a blank session once resources are ready. Do not depend on
  // createBlankSession/resetBuilder identities — those change when locations
  // or properties refetch and would wipe depot edits mid-session.
  useEffect(() => {
    if (!blankSeason || isLoadingResources) return;
    resetBuilder();
    createBlankSession(blankSeason);
    setModal("adjust");
    onBlankSessionStarted?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blankSeason, isLoadingResources]);

  // CT: restore the builder without overriding an open configuration modal.
  useEffect(() => {
    if (syncUrl) return;
    if (session && modal === "none") setModal("adjust");
  }, [syncUrl, session, modal, setModal]);

  const searchKey =
    typeof searchParams.toString === "function" ? searchParams.toString() : "";

  useEffect(() => {
    if (!syncUrl || !urlReady) return;
    const next = new URLSearchParams(searchKey);
    const previous = next.toString();
    if (modal === "none") {
      next.delete("builder");
      next.delete("builderDraft");
      next.delete("builderMode");
      next.delete("builderRoutes");
    } else {
      next.set("builder", modal);
      // Keep the draft link while configuration is open so refresh restores it.
      if (activeDraftId) {
        next.set("builderDraft", activeDraftId);
      } else {
        next.delete("builderDraft");
      }
      if (modal === "adjust" && builderMode === "edit" && editRouteIds) {
        next.set("builderMode", "edit");
        next.set("builderRoutes", editRouteIds);
      } else {
        next.delete("builderMode");
        next.delete("builderRoutes");
      }
    }
    if (next.toString() === previous) return;
    setSearchParams(next, { replace: true });
  }, [
    modal,
    activeDraftId,
    builderMode,
    editRouteIds,
    urlReady,
    searchKey,
    setSearchParams,
    syncUrl,
  ]);

  const closeAll = async () => {
    if (hasUnsavedSessionChanges) {
      const ok = await multipleChoiceConfirmWrapper("Close Route Builder?", {
        subtitle:
          "You have unsaved route adjustments. Close Route Builder anyway?",
        confirmLabel: "Close builder",
      });
      if (!ok) return;
    }
    resetBuilder();
    setModal("none");
  };

  const closeConfiguration = () => {
    // Dismiss the overlay without clearing the current plan or its unsaved edits.
    if (session) setModal("adjust");
    else void closeAll();
  };

  return (
    <>
      {renderButton ? (
        <GenerateRoutesControl
          onOpenSetup={openSetup}
          showNotice={showNotice}
        />
      ) : null}
      {showNotice ? <OptimizationNotice /> : null}
      {/* Initial setup stays over Routes; existing plans stay mounted under configuration. */}
      {modal === "adjust" || (modal === "setup" && session) ? (
        <AdjustRouteBuilderModal
          onClose={() => void closeAll()}
          onOpenConfiguration={() => setModal("setup")}
          configurationOpen={modal === "setup"}
        />
      ) : null}
      {modal === "setup" ? (
        <SetupRouteBuilderModal onClose={closeConfiguration} />
      ) : null}
    </>
  );
}

/** Routes-page entry: Generate Routes button + builder and configuration overlay. */
const RouteBuilderEntry = forwardRef<
  RouteBuilderEntryHandle,
  {
    initialModal?: RouteBuilderModal;
    onRoutesCreated?: () => void;
    blankSeason?: Season | null;
    onBlankSessionStarted?: () => void;
    /** Keep the address bar in sync. CT sets this false so writes cannot remount the iframe. */
    syncUrl?: boolean;
    renderButton?: boolean;
    /** Called when the out-of-credits notice shows or hides. */
    onOptimizationNoticeChange?: (visible: boolean) => void;
  }
>(function RouteBuilderEntry(
  {
    initialModal = "none",
    onRoutesCreated,
    blankSeason = null,
    onBlankSessionStarted,
    syncUrl = true,
    renderButton = true,
    onOptimizationNoticeChange,
  },
  ref,
) {
  const [modal, setModal] = useState<RouteBuilderModal>(initialModal);

  return (
    <RouteBuilderProvider
      onRoutesGenerated={() => setModal("adjust")}
      onRoutesCreated={() => {
        setModal("none");
        onRoutesCreated?.();
      }}
    >
      <RouteBuilderEntryInner
        modal={modal}
        setModal={setModal}
        blankSeason={blankSeason}
        onBlankSessionStarted={onBlankSessionStarted}
        entryRef={ref}
        syncUrl={syncUrl}
        renderButton={renderButton}
        onOptimizationNoticeChange={onOptimizationNoticeChange}
      />
    </RouteBuilderProvider>
  );
});

export default RouteBuilderEntry;
